import React from 'react';
import { observer } from 'mobx-react';
import { Button, Flex, Icon, Tag, Text } from 'core/components';
import { TbSubtask } from 'react-icons/tb';
import Markdown from 'app/components/markdown/Markdown';
import { SmallHeading, SubAgentPanelCard, SubAgentPanelHeader } from '../KaizenChatSession.styles';
import SubAgentRequest from './SubAgentRequest';
import SubAgentTranscriptStep from './SubAgentTranscriptStep';
import ThinkingIndicator from './ThinkingIndicator';
import KaizenErrorMessage from './KaizenErrorMessage';
import { getAgentDisplayName } from '../util/agentDisplayNames';
import { isErroredToolCall } from '../util/toolErrorUtils';
import { isSubAgentRunInFlight, resolveSubAgentRun } from '../util/turnStepGroups';

const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

function parseRawResult(rawResult) {
  if (!rawResult) {
    return {};
  }
  if (typeof rawResult === 'string') {
    try {
      return JSON.parse(rawResult);
    } catch (e) {
      return {};
    }
  }
  return rawResult;
}

/**
 * Combined view for one sub-agent invocation: the instructions it was given, every step it
 * took with tool inputs/outputs, and the answer it handed back.
 */
const SubAgentRunPanel = observer(({ tool, sessionModel, onCloseToolResult }) => {
  const { steps, turn, toolCall } = resolveSubAgentRun({ sessionModel, toolCall: tool });

  // The selected tool is a snapshot from click time; the model replaces it on every update.
  const liveTool = toolCall || tool;
  const agentName = liveTool.tool_name;

  const isProcessing = isSubAgentRunInFlight({ steps, turn }) || !liveTool.status;
  const isFailed = isErroredToolCall(liveTool);

  const rawData = parseRawResult(liveTool.raw_result);
  const lastStepAnswer = steps[steps.length - 1]?.llm_response?.final_answer;
  // A failed run's processed_result is a copy of its error, which the Error section shows
  const processedAnswer = isFailed ? null : liveTool.processed_result;
  const finalAnswer = rawData.finalAnswer || processedAnswer || lastStepAnswer;
  const toolCallCount = steps.reduce((total, s) => total + (s.toolCalls?.length || 0), 0);

  const summary = [pluralize(steps.length, 'step'), pluralize(toolCallCount, 'tool call')].join(' · ');

  const statusText = (() => {
    if (isProcessing) {
      return 'Working…';
    }
    return isFailed ? 'Failed' : 'Completed';
  })();

  return (
    <SubAgentPanelCard className="tool-result-sub-agent-run">
      <SubAgentPanelHeader>
        <Icon icon={TbSubtask} size={15} color="primary" />
        <Text muted>Subagent:</Text>
        <Text fontWeight="medium">{getAgentDisplayName(agentName)}</Text>
        <Text small muted>
          · {summary} · {statusText}
        </Text>
        <Button
          text="Close"
          small
          icon="cross"
          minimal
          color="muted"
          ml="auto"
          onClick={() => onCloseToolResult(tool)}
        />
      </SubAgentPanelHeader>

      <Flex flexDirection="column" p={2} flex={1} overflow="auto" gap={2}>
        <SubAgentRequest queryObject={liveTool.query_object} />

        <Flex flexDirection="column" gap={1}>
          <Flex alignItems="center" gap={1}>
            <SmallHeading>Activity</SmallHeading>
            <Tag minimal round>
              {steps.length}
            </Tag>
          </Flex>

          {steps.length > 0 ? (
            steps.map((step, index) => (
              // The last step's final_answer is what the tool returned — shown in its own section below
              <SubAgentTranscriptStep
                key={step.id || index}
                step={step}
                isRunActive={isProcessing}
                showFinalAnswer={false}
                sessionModel={sessionModel}
              />
            ))
          ) : (
            <Text muted small>
              No step detail was recorded for this run.
            </Text>
          )}

          {isProcessing && <ThinkingIndicator />}
        </Flex>

        {finalAnswer && (
          <Flex flexDirection="column" gap={1}>
            <SmallHeading>Final answer</SmallHeading>
            <Markdown>{finalAnswer}</Markdown>
          </Flex>
        )}

        {isFailed && liveTool.error_message && (
          <Flex flexDirection="column" gap={1}>
            <SmallHeading>Error</SmallHeading>
            <Text small color="danger">
              <KaizenErrorMessage message={liveTool.error_message} />
            </Text>
          </Flex>
        )}
      </Flex>
    </SubAgentPanelCard>
  );
});

SubAgentRunPanel.displayName = 'SubAgentRunPanel';

export default SubAgentRunPanel;
